import { useMemo } from 'react'
import type { AberturaSaida } from '../dominio/didatico/aberturas'
import type { FolgaVertical, ObstaculoRota } from '../dominio/didatico/obstaculosRota'
import { rotuloAcao, type AcaoPeca } from '../dominio/didatico/estadoPecas'
import { ESTADOS_TETO, tecnicaDeTeto, type EstadoTeto } from '../dominio/didatico/teto'
import { acoesDisponiveis } from '../dominio/didatico/catalogo'
import {
  ANGULOS_NOMINAIS,
  leituraDaRota,
  type LeituraDaRota,
  type AnguloNominal,
  type RotaExtracao,
} from '../dominio/didatico/rotasExtracao'
import type { CustoDoGiro } from '../dominio/didatico/custoDoGiro'
import {
  CONFIGURACOES_COM_EXTRACAO,
  DECUBITOS,
  NOMES_CONFIGURACAO,
  NOMES_DECUBITO,
  NOMES_SENTIDO_CABECA,
  posicaoAberturaNoTombado,
  type ConfiguracaoVeiculo,
  type Decubito,
  type SentidoCabeca,
} from '../dominio/didatico/configuracaoVeiculo'
import { decomporAngulosCervicais, type EixoColuna } from '../dominio/geometria/anguloExtracao'
import { direcaoDaSeta, type OrientacaoSeta } from '../dominio/didatico/setaCervical'
import { NOMES_OBSTACULO_EXTERNO, type ObstaculoExterno, type TipoObstaculoExterno } from '../dominio/didatico/obstaculosExternos'
import type { FerramentaExtracao } from '../visualizacao3d/extracao/CenaExtracao'
import { indiceEstabilidadeExtracao } from '../dominio/didatico/meiaPrancha'

export type ModoRota = 'abertura' | 'livre' | 'nominal'
export type SecaoPainelExtracao =
  | 'posicao'
  | 'rota'
  | 'obstaculos'
  | 'tetoTecnica'
  | 'sobreposicoes'
  | 'resultados'

interface Props {
  /** Limita o conteúdo exibido; sem esta prop, o painel completo é preservado. */
  secoes?: readonly SecaoPainelExtracao[]
  modoRota: ModoRota
  aberturas: AberturaSaida[]
  idAbertura: string
  anguloSelecionado: AnguloNominal
  idComparada: string | null
  rota: RotaExtracao
  obstaculos: ObstaculoRota[]
  /** folga vertical sob os bancos; só vale com o veículo invertido */
  folgaVertical: FolgaVertical | null
  /** o que esta rota custa à vítima em movimento executado sobre ela */
  custo: CustoDoGiro
  /** as quatro rotas nominais com giro e folga lado a lado */
  comparativo: readonly {
    anguloNominal: AnguloNominal
    leitura: LeituraDaRota
    custo: CustoDoGiro
    folga: number
  }[]
  /** o corpo é girado em bloco: sem torção residual na cervical */
  girarEmBloco: boolean
  aoAlternarBloco: (v: boolean) => void
  configuracao: ConfiguracaoVeiculo
  decubito: Decubito
  sentidoCabeca: SentidoCabeca
  comPrancha: boolean
  comMeiaPrancha?: boolean
  colarCervical?: boolean
  aoAlternarMeiaPrancha?: (v: boolean) => void
  aoAlternarColarCervical?: (v: boolean) => void
  modoConceito: boolean
  /** carroceria translúcida (ver o interior) — comando manual, não remove nem reposiciona nada. */
  verInterior: boolean
  estadoTeto: EstadoTeto
  /** variação da folga de cabeça em relação ao veículo íntegro; null = sem teto */
  ganhoDeEspaco: number | null
  mostrarEixoColuna: boolean
  mostrarTrajetoria: boolean
  mostrarArco: boolean
  mostrarPosicaoInicial: boolean
  eixoColuna: EixoColuna
  orientacaoSeta: OrientacaoSeta
  mostrarSetaCervical: boolean
  ferramenta?: FerramentaExtracao
  pontosRotaLivre?: number
  obstaculosExternos: readonly ObstaculoExterno[]
  idsObstaculosInterferindo: readonly string[]
  vitimasInterferindo: readonly { id: string; nome: string }[]
  prioridadePendente: boolean
  aoTrocarModo: (m: ModoRota) => void
  aoEscolherAbertura: (id: string) => void
  aoEscolherAngulo: (a: AnguloNominal) => void
  aoComparar: (id: string | null) => void
  aoAlternarPrancha: (v: boolean) => void
  aoAlternarConceito: (v: boolean) => void
  aoAlternarVerInterior: (v: boolean) => void
  aoEscolherTeto: (e: EstadoTeto) => void
  aoEscolherConfiguracao: (c: ConfiguracaoVeiculo) => void
  aoEscolherDecubito: (d: Decubito) => void
  aoEscolherSentidoCabeca: (s: SentidoCabeca) => void
  /** resolve um obstáculo de interior direto da lista */
  aoAgirNoObstaculo: (id: string, acao: AcaoPeca) => void
  aoAlternar: (
    chave: 'mostrarEixoColuna' | 'mostrarTrajetoria' | 'mostrarArco' | 'mostrarPosicaoInicial',
    valor: boolean,
  ) => void
  aoAlterarOrientacaoSeta: (orientacao: OrientacaoSeta) => void
  aoAlternarSetaCervical: (valor: boolean) => void
  aoAlinharSetaComRota: () => void
  aoEscolherFerramenta: (ferramenta: FerramentaExtracao) => void
  aoLimparTrajeto?: () => void
  aoLimparObstaculosExternos: () => void
}

const graus = (n: number) => `${n.toFixed(0)}°`

const cm = (m: number) => `${(m * 100).toFixed(0)} cm`

const classeDoGanho = (ganho: number | null): string => {
  if (ganho === null) return 'aberto'
  if (ganho < 0) return 'perda'
  return ganho > 0 ? 'ganho' : ''
}

const NOME_FAIXA_CERVICAL: Record<ReturnType<typeof decomporAngulosCervicais>['faixa'], string> = {
  alinhado: 'alinhado',
  atencao: 'atenção',
  excessivo: 'excessivo',
}

const CLASSE_FAIXA_CERVICAL: Record<ReturnType<typeof decomporAngulosCervicais>['faixa'], string> = {
  alinhado: 'livre',
  atencao: 'apertado',
  excessivo: 'bloqueio',
}

export function PainelExtracao(props: Props) {
  const leitura = leituraDaRota(props.rota)
  const tombado = props.configuracao === 'lateral'
  const invertido = props.configuracao === 'invertido'
  const paDeForno = tombado || invertido
  const relevantes = props.obstaculos.filter((o) => o.interfere || o.folga < 0.08)
  const estruturais = relevantes.filter((o) => o.tipo === 'estrutura')
  const interiores = relevantes.filter((o) => o.tipo === 'interior')
  const requisito = props.rota.abertura?.requisito
  const exibe = (secao: SecaoPainelExtracao) => !props.secoes || props.secoes.includes(secao)

  const angulosCervicais = useMemo(
    () => decomporAngulosCervicais(props.eixoColuna, direcaoDaSeta(props.eixoColuna, props.orientacaoSeta)),
    [props.eixoColuna, props.orientacaoSeta],
  )

  return (
    <>
      {exibe('posicao') && (
      <div className="grupo-controle">
        <span className="titulo-controle">Como o veículo parou</span>
        <div className="linha-botoes">
          {CONFIGURACOES_COM_EXTRACAO.map((c) => (
            <button
              key={c}
              className={props.configuracao === c ? 'ativo' : ''}
              onClick={() => props.aoEscolherConfiguracao(c)}
            >
              {NOMES_CONFIGURACAO[c]}
            </button>
          ))}
        </div>
        {invertido ? (
          <>
            <p className="dica">
              No capotamento a retirada é feita como se a vítima estivesse deitada no teto — que virou
              o piso. Não suspensa pelo cinto. O corpo estendido é mais longo que o teto da cabine:
              num ocupante dianteiro ele avança sobre a área do para-brisa, como no veículo real.
            </p>
            <label className="campo">
              <span>decúbito</span>
              <select
                value={props.decubito}
                onChange={(e) => props.aoEscolherDecubito(e.target.value as Decubito)}
              >
                {DECUBITOS.map((d) => (
                  <option key={d} value={d}>
                    {NOMES_DECUBITO[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span>cabeça</span>
              <select
                value={props.sentidoCabeca}
                onChange={(e) => props.aoEscolherSentidoCabeca(e.target.value as SentidoCabeca)}
              >
                {(['frente', 'traseira'] as const).map((v) => (
                  <option key={v} value={v}>
                    {NOMES_SENTIDO_CABECA[v]}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : tombado ? (
          <p className="dica">
            O veículo está apoiado sobre o lado esquerdo. A vítima está deitada sobre a lateral
            baixa, que virou o piso da cabine. A retirada é preferencialmente pela traseira/porta-malas. Portas do lado alto são alternativas; as portas esquerdas permanecem indisponíveis contra o solo.
          </p>
        ) : (
          <p className="dica">
            A vítima está sentada na âncora.
          </p>
        )}
      </div>
      )}

      {exibe('rota') && (
      <>
      <div className="grupo-controle">
        <span className="titulo-controle">Como definir a rota</span>
        <div className="linha-botoes">
          <button
            className={props.modoRota === 'abertura' ? 'ativo' : ''}
            onClick={() => props.aoTrocarModo('abertura')}
          >
            Por abertura
          </button>
          <button
            className={props.modoRota === 'livre' ? 'ativo' : ''}
            onClick={() => props.aoTrocarModo('livre')}
          >
            Seta livre
          </button>
          <button
            className={props.modoRota === 'nominal' ? 'ativo' : ''}
            onClick={() => props.aoTrocarModo('nominal')}
          >
            Demonstração
          </button>
        </div>
        <p className="dica">
          {props.modoRota === 'abertura'
            ? 'A vítima sai por uma abertura real. O ângulo é consequência do banco e da saída — não é escolhido.'
            : props.modoRota === 'livre'
              ? 'Arraste a alça na ponta da seta na cena 3D para direcionar a saída e medir os ângulos em tempo real.'
              : 'Ângulos de referência 0/30/60/90, para apresentar o conceito antes de aplicar ao veículo.'}
        </p>
      </div>

      {props.modoRota === 'abertura' ? (
        <div className="grupo-controle">
          <span className="titulo-controle">Sair por</span>
          <div className="grade-aberturas">
            {props.aberturas.map((a) => {
              const posicao = tombado ? posicaoAberturaNoTombado(a.lado) : null
              const contraSolo = posicao === 'contraSolo'
              return (
              <button
                key={a.id}
                className={`${props.idAbertura === a.id ? 'ativo' : ''} ${contraSolo ? 'abertura-solo' : ''}`}
                onClick={() => props.aoEscolherAbertura(a.id)}
                disabled={contraSolo}
                title={contraSolo ? 'Indisponível: esta porta está contra o solo' : undefined}
              >
                {a.curto}
                {posicao && <small>{posicao === 'ladoAlto' ? 'lado alto' : posicao === 'contraSolo' ? 'contra o solo' : 'traseira'}</small>}
              </button>
              )
            })}
          </div>
          <p className="instrucao-clique">
            <strong>
              Ângulo de retirada: {leitura.axial ? '0° (retirada axial)' : `${leitura.convencao}° (${Math.round(leitura.exatoGraus)}° em relação ao eixo sagital)`}
            </strong>
          </p>
          {tombado && (
            <p className="dica">
              O modelo impede saídas pelo lado apoiado no solo. Priorize a traseira/porta-malas; use o lado alto quando a rota justificar. Saída pelo teto exige corte, e o para-brisa não é rota didática devido ao risco do vidro.
            </p>
          )}
        </div>
      ) : props.modoRota === 'nominal' ? (
        <div className="grupo-controle">
          <span className="titulo-controle">Ângulo de referência</span>
          <div className="grade-angulos">
            {ANGULOS_NOMINAIS.map((valor) => (
              <button
                key={valor}
                className={props.anguloSelecionado === valor ? 'ativo' : ''}
                onClick={() => props.aoEscolherAngulo(valor)}
              >
                {valor}°
              </button>
            ))}
          </div>
          <p className="instrucao-clique">
            <strong>
              Ângulo de retirada: {props.anguloSelecionado === 0 ? '0° (retirada axial)' : `${props.anguloSelecionado}°`}
            </strong>
          </p>
        </div>
      ) : (
        <div className="grupo-controle">
          <span className="titulo-controle">Seta direcional interativa</span>
          <p className="instrucao-clique">
            <strong>
              Ângulo de retirada: {leitura.axial ? '0° (retirada axial)' : `${Math.round(props.rota.resultado.anguloGraus ?? leitura.exatoGraus)}° em relação ao eixo sagital`}
            </strong>
            Arraste a esfera na ponta da seta na cena 3D para explorar rotas de extração em tempo real e observar a angulação e a abertura alinhada.
          </p>
        </div>
      )}

      {requisito && requisito.tecnica !== 'nenhuma' && (
        <p className={`requisito ${requisito.tecnica === 'corteTampaETeto' ? 'corte' : ''}`}>
          <strong>{requisito.tecnica === 'corteTampaETeto' ? 'Exige corte' : 'Antes da retirada'}</strong>
          {requisito.texto}
        </p>
      )}
      </>
      )}

      {exibe('resultados') && (paDeForno ? (
        <div className="grupo-controle">
          <span className="titulo-controle">Retirada em bloco sobre a prancha</span>
          <strong>Sem giro da vítima</strong>
          <p className="dica">Na pá de forno a vítima permanece alinhada. A prancha conduz o conjunto até a saída; isso não é ângulo de extração aplicado à coluna.</p>
          <p className="dica">Compare abertura, espaço e obstáculos. A saída preferencial é definida pelo cenário e pela segurança da rota.</p>
        </div>
      ) : (
      <>
      <div className={`leitura-angulo giro-${leitura.faixa}`}>
        <span className="titulo-controle">Ângulo de trabalho</span>
        <strong>{leitura.axial ? 'retirada axial' : `${leitura.convencao}°`}</strong>
        <span className="calculado">calculado {leitura.exatoGraus.toFixed(0)}°</span>
        <p>
          {leitura.axial
            ? 'o corpo sai pelo próprio eixo: nada a girar na coluna cervical'
            : props.rota.resultado.lado
              ? `para o lado ${props.rota.resultado.lado} da vítima`
              : 'sem rotação a executar'}
        </p>
        <dl className="custo-giro">
          <div>
            <dt>giro sobre a vítima</dt>
            <dd>{props.custo.giroGraus.toFixed(0)}°</dd>
          </div>
          <div>
            <dt>arco dos ombros</dt>
            <dd>{cm(props.custo.arcoOmbrosCm / 100)}</dd>
          </div>
          <div>
            <dt>torção na cervical</dt>
            <dd>
              {props.girarEmBloco
                ? 'nenhuma — corpo em bloco'
                : `${props.custo.torcaoCervicalGraus.toFixed(0)}°`}
            </dd>
          </div>
        </dl>
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.girarEmBloco}
            onChange={(e) => props.aoAlternarBloco(e.target.checked)}
          />
          girar o corpo em bloco
        </label>
        <p className="dica">
          Quanto maior o ângulo, mais caminho os ombros da vítima percorrem e mais rotação a
          equipe executa sobre a coluna — é por isso que o giro se evita. Movendo o corpo em
          bloco, pelve, tronco e cabeça giram juntos e não sobra torção na cervical; sem isso,
          parte do giro é paga pelo pescoço. Faixas e limiares são convenção didática pendente
          de confirmação do instrutor, como o limiar de retirada axial.
        </p>
        <p className="dica">
          {leitura.medida === 'rotacaoTransversal'
            ? 'Medido entre o eixo da coluna da vítima sentada e a direção da saída, no plano transversal, e aproximado para a convenção de trabalho.'
            : 'Deitada, o ângulo transversal vale 90° para toda saída e não diz nada. O que vale é o desvio entre a direção do puxão e o eixo do corpo — perto de zero, a retirada é axial.'}
        </p>
      </div>

      <div className="grupo-controle">
        <span className="titulo-controle">Giro × folga nas quatro rotas</span>
        <table className="comparativo-giro">
          <thead>
            <tr>
              <th>ângulo</th>
              <th>ombros</th>
              <th>cervical</th>
              <th>folga</th>
            </tr>
          </thead>
          <tbody>
            {props.comparativo.map((linha) => (
              <tr
                key={linha.anguloNominal}
                className={[
                  `giro-${linha.custo.faixa}`,
                  props.modoRota === 'nominal' && props.anguloSelecionado === linha.anguloNominal
                    ? 'ativo'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <th scope="row">{linha.leitura.axial ? 'axial' : `${linha.anguloNominal}°`}</th>
                <td>{cm(linha.custo.arcoOmbrosCm / 100)}</td>
                <td>{props.girarEmBloco ? '—' : `${linha.custo.torcaoCervicalGraus.toFixed(0)}°`}</td>
                <td className={linha.folga < 0 ? 'interfere' : ''}>
                  {Number.isFinite(linha.folga)
                    ? linha.folga < 0
                      ? `−${cm(-linha.folga)}`
                      : cm(linha.folga)
                    : 'livre'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dica">
          Ângulo menor NÃO é rota mais livre: a rota de menor giro pode ser justamente a que
          esbarra na coluna B. As duas colunas ficam lado a lado para que a escolha pese giro e
          folga juntos, e não uma só.
        </p>
      </div>
      </>
      ))}
      {exibe('obstaculos') && (
      <>
      <div className="grupo-controle">
        <span className="titulo-controle">Obstáculos externos</span>
        <div className="grade-obstaculos-externos">
          {(Object.keys(NOMES_OBSTACULO_EXTERNO) as TipoObstaculoExterno[]).map((tipo) => (
            <button
              key={tipo}
              className={props.ferramenta === tipo ? 'ativo' : ''}
              onClick={() => props.aoEscolherFerramenta(tipo)}
            >
              {NOMES_OBSTACULO_EXTERNO[tipo]}
            </button>
          ))}
        </div>
        <p className="instrucao-clique">
          <strong>Selecione e clique próximo ao carro</strong>
          Posicione outro veículo, poste ou muro. Clique no próprio obstáculo para removê-lo.
        </p>
        {props.obstaculosExternos.length > 0 && (
          <button onClick={props.aoLimparObstaculosExternos}>Remover obstáculos externos</button>
        )}
      </div>

      {(props.prioridadePendente || props.vitimasInterferindo.length > 0 || props.idsObstaculosInterferindo.length > 0) && (
        <div className="grupo-controle alerta-extracao">
          <span className="titulo-controle">Replanejar antes de retirar</span>
          {props.prioridadePendente && (
            <p className="obstaculo apertado"><strong>Ordem tática escolhida</strong>Há vítima grave aguardando. Retirar a estável primeiro pode ser necessário para liberar acesso ou corredor; confirme a justificativa antes de executar.</p>
          )}
          {props.vitimasInterferindo.map((vitima) => (
            <p key={vitima.id} className="obstaculo apertado"><strong>{vitima.nome}</strong>Outra vítima ocupa o corredor da trajetória.</p>
          ))}
          {props.obstaculosExternos.filter((o) => props.idsObstaculosInterferindo.includes(o.id)).map((o) => (
            <p key={o.id} className="obstaculo bloqueio"><strong>{NOMES_OBSTACULO_EXTERNO[o.tipo]}</strong>Impede ou estreita a rota desenhada.</p>
          ))}
          <p className="dica">A gravidade sugere prioridade clínica, mas não bloqueia a sequência. A ordem final também considera acesso, aprisionamento, riscos da cena e se uma vítima impede a retirada da outra.</p>
        </div>
      )}

      <div className="grupo-controle">
        <span className="titulo-controle">O que está no caminho</span>

        {estruturais.length === 0 && interiores.length === 0 && (
          <p className="obstaculo livre">Nada interfere nesta rota.</p>
        )}

        {estruturais.map((o) => (
          <p key={o.idPeca} className={`obstaculo ${o.interfere ? 'bloqueio' : 'apertado'}`}>
            <strong>{o.nome}</strong>
            {o.interfere ? `interferência de ${cm(-o.folga)} — exige corte` : `${cm(o.folga)} de folga`}
          </p>
        ))}

        {interiores.map((o) => (
          <div key={o.idPeca} className={`obstaculo interior ${o.interfere ? 'bloqueio' : 'apertado'}`}>
            <strong>{o.nome}</strong>
            <span>
              {o.interfere ? `interferência de ${cm(-o.folga)}` : `${cm(o.folga)} de folga`}
            </span>
            {/* o aluno resolve interior na hora: reclinar, recuar, remover */}
            <div className="acoes-obstaculo">
              {acoesDisponiveis(o.idPeca)
                .filter((a) => a !== 'destacar' && a !== 'restaurar')
                .map((acao) => (
                  <button key={acao} onClick={() => props.aoAgirNoObstaculo(o.idPeca, acao)}>
                    {rotuloAcao(acao)}
                  </button>
                ))}
            </div>
          </div>
        ))}

        {props.folgaVertical && (
          <p className={`obstaculo ${props.folgaVertical.folga < 0 ? 'bloqueio' : 'apertado'}`}>
            <strong>Vão entre o teto e {props.folgaVertical.nome.toLowerCase()}</strong>
            {`${cm(props.folgaVertical.vao)} de vão, ${
              props.folgaVertical.folga < 0
                ? `${cm(-props.folgaVertical.folga)} a menos que a espessura do corpo`
                : `${cm(props.folgaVertical.folga)} de folga sobre a espessura do corpo`
            }`}
          </p>
        )}

        <p className="dica">
          {invertido
            ? 'Deitada no teto, a vítima não disputa espaço lateral com os bancos: eles ficam acima dela. O que decide é o vão VERTICAL entre o teto e a peça mais baixa no caminho — reclinar o encosto ou tirar o apoio de cabeça muda esse número na hora.'
            : tombado
              ? 'No tombado, a rota é calculada no referencial da cabine e depois girada com o veículo. O lado esquerdo está contra o solo; use o lado alto ou a traseira e confira as interferências internas antes de deslocar.'
            : 'Folga entre a peça e os ombros da vítima, no plano horizontal. Estrutura exige corte; interior a equipe resolve na hora. É estimativa didática, não medição de colisão.'}
        </p>
      </div>
      </>
      )}

      {exibe('tetoTecnica') && (
      <>
      <div className="grupo-controle">
        <span className="titulo-controle">Teto</span>
        <div className="grade-teto">
          {ESTADOS_TETO.map((estado) => {
            const t = tecnicaDeTeto(estado)
            return (
              <button
                key={estado}
                className={`${props.estadoTeto === estado ? 'ativo' : ''} ${t.eDano ? 'dano' : ''}`}
                onClick={() => props.aoEscolherTeto(estado)}
              >
                {t.nome}
              </button>
            )
          })}
        </div>
        <p className="dica">{tecnicaDeTeto(props.estadoTeto).descricao}</p>
        <p className={`espaco ${classeDoGanho(props.ganhoDeEspaco)}`}>
          <strong>Espaço sobre a vítima</strong>
          {props.ganhoDeEspaco === null
            ? 'sem teto acima: a altura deixa de limitar'
            : props.ganhoDeEspaco === 0
              ? 'como o veículo íntegro'
              : `${props.ganhoDeEspaco > 0 ? '+' : '−'}${cm(Math.abs(props.ganhoDeEspaco))} em relação ao íntegro`}
        </p>
      </div>

      <div className="grupo-controle">
        <span className="titulo-controle">Técnica</span>
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.comPrancha || paDeForno}
            disabled={paDeForno}
            onChange={(e) => props.aoAlternarPrancha(e.target.checked)}
          />
          {paDeForno ? 'pá de forno com prancha longa' : 'prancha longa: deitar antes de retirar'}
        </label>
        <p className="dica">
          {paDeForno
            ? 'Pá de forno: elevar a vítima em bloco → introduzir a prancha por baixo → abaixar a vítima sobre a prancha → alinhar → retirar a prancha com a vítima.'
            : 'Com a prancha, a sequência é colar cervical → apoio de cabeça → reclinar banco → prancha → alinhar → deitar → deslocar. O ângulo continua sendo o da posição sentada inicial.'}
        </p>

        {/* Estabilização precoce com colar cervical e meia-prancha */}
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.colarCervical ?? false}
            onChange={(e) => props.aoAlternarColarCervical?.(e.target.checked)}
          />
          colar cervical de resgate (alinhamento rígido)
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.comMeiaPrancha ?? false}
            onChange={(e) => props.aoAlternarMeiaPrancha?.(e.target.checked)}
          />
          meia-prancha dorsal (estabilização tóraco-lombar prévia)
        </label>
        {(props.comMeiaPrancha || props.colarCervical) && (
          <p className="dica" style={{ color: '#38bdf8' }}>
            {indiceEstabilidadeExtracao(
              { posicionada: props.comMeiaPrancha ?? false, cintasFixadas: true },
              props.colarCervical ?? false,
            ).leitura}
          </p>
        )}
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.verInterior}
            onChange={(e) => props.aoAlternarVerInterior(e.target.checked)}
          />
          ver o interior (carroceria translúcida)
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.modoConceito}
            onChange={(e) => props.aoAlternarConceito(e.target.checked)}
          />
          modo conceito: só vítima, carro e obstáculos
        </label>
      </div>
      </>
      )}

      {exibe('sobreposicoes') && (
      <>
      <div className="grupo-controle">
        <span className="titulo-controle">Comparar com</span>
        <select
          value={props.idComparada ?? ''}
          disabled={props.modoRota === 'livre'}
          onChange={(e) => props.aoComparar(e.target.value === '' ? null : e.target.value)}
          aria-label="Segunda rota para comparação"
        >
          <option value="">nenhuma</option>
          {props.modoRota === 'livre' ? null : props.modoRota === 'abertura'
            ? props.aberturas
                .filter((a) => a.id !== props.idAbertura)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))
            : ANGULOS_NOMINAIS.filter((a) => a !== props.anguloSelecionado).map((a) => (
                <option key={a} value={String(a)}>
                  {a}°
                </option>
              ))}
        </select>
        <p className="dica">{props.modoRota === 'livre' ? 'A comparação fica indisponível durante o desenho livre.' : 'A comparação usa o mesmo veículo, a mesma vítima e o mesmo instante.'}</p>
      </div>

      <div className="grupo-controle">
        <span className="titulo-controle">Sobreposições</span>
        {(
          [
            ['mostrarEixoColuna', 'eixo da coluna'],
            ['mostrarTrajetoria', 'trajetória e ponto de saída'],
            ['mostrarArco', 'arco do ângulo'],
            ['mostrarPosicaoInicial', 'posição inicial da vítima'],
          ] as const
        ).map(([chave, rotulo]) => (
          <label key={chave} className="toggle">
            <input type="checkbox" checked={props[chave]} onChange={(e) => props.aoAlternar(chave, e.target.checked)} />
            {rotulo}
          </label>
        ))}
        <label className="toggle">
          <input
            type="checkbox"
            checked={props.mostrarSetaCervical}
            onChange={(e) => props.aoAlternarSetaCervical(e.target.checked)}
          />
          seta cervical (arrastável)
        </label>
      </div>

      {props.mostrarSetaCervical && (
        <div className="grupo-controle">
          <span className="titulo-controle">Ângulos cervicais</span>
          <p className="dica">
            Arraste a ponta da seta na cena — apoiada na base da coluna cervical — ou use os
            controles abaixo. Os quatro números vêm da mesma direção.
          </p>
          <dl className="leituras">
            <div>
              <dt>flexão / extensão</dt>
              <dd>{graus(angulosCervicais.flexaoExtensaoGraus)}</dd>
            </div>
            <div>
              <dt>inclinação lateral</dt>
              <dd>{graus(angulosCervicais.inclinacaoLateralGraus)}</dd>
            </div>
            <div>
              <dt>rotação</dt>
              <dd>{graus(angulosCervicais.rotacaoGraus)}</dd>
            </div>
            <div>
              <dt>desvio total</dt>
              <dd>{graus(angulosCervicais.desvioTotalGraus)}</dd>
            </div>
          </dl>
          <p className={`obstaculo ${CLASSE_FAIXA_CERVICAL[angulosCervicais.faixa]}`}>
            <strong>Faixa: {NOME_FAIXA_CERVICAL[angulosCervicais.faixa]}</strong>
            Convenção didática (≤15° alinhado, ≤30° atenção), pendente de confirmação do instrutor —
            não é limiar clínico.
          </p>
          <label className="deslizante">
            <span>
              azimute <strong>{graus(props.orientacaoSeta.azimuteGraus)}</strong>
            </span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={props.orientacaoSeta.azimuteGraus}
              onChange={(e) =>
                props.aoAlterarOrientacaoSeta({ ...props.orientacaoSeta, azimuteGraus: Number(e.target.value) })
              }
            />
          </label>
          <label className="deslizante">
            <span>
              elevação <strong>{graus(props.orientacaoSeta.elevacaoGraus)}</strong>
            </span>
            <input
              type="range"
              min={0}
              max={90}
              step={1}
              value={props.orientacaoSeta.elevacaoGraus}
              onChange={(e) =>
                props.aoAlterarOrientacaoSeta({ ...props.orientacaoSeta, elevacaoGraus: Number(e.target.value) })
              }
            />
          </label>
          <button onClick={props.aoAlinharSetaComRota}>Alinhar com a rota em análise</button>
        </div>
      )}
      </>
      )}
    </>
  )
}
